// Declarations handed to Monaco for the modules Bonsai Web scripts import.
// Types for "ifcopenshell" itself come from the package's own .d.ts files
// (core/editor.js).
// Keep in step with bonsai.js.

export const TYPINGS = `
/** One drawn object, as returned by viewer.getObjects(). */
interface ViewerObject {
  objectId: number;
  guid: string;
  name: string;
  type: string;
  model: number;
  sourceId: number;
}

/** objectIds, GlobalIds, or ViewerObjects. One or many. */
type Target = number | string | ViewerObject | Array<number | string | ViewerObject>;

/** '#rgb', '#rrggbb', '#rrggbbaa', or channels 0-255. */
type Colour = string | { r: number; g: number; b: number; a?: number };

interface Camera {
  target: [number, number, number];
  distance: number;
  /** Degrees about world +Z. */
  yaw: number;
  /** Degrees above the XY plane. */
  pitch: number;
  eye: [number, number, number];
  ortho: boolean;
}

/** The WebGPU IfcViewer. */
interface IfcViewer {
  getObjects(): Promise<ViewerObject[]>;
  getSelection(): number[];
  getSelectedObjects(): ViewerObject[];
  setSelection(target: Target): void;
  addToSelection(target: Target): void;
  removeFromSelection(target: Target): void;
  clearSelection(): void;
  show(target: Target): void;
  hide(target: Target): void;
  setVisible(target: Target, visible: boolean): void;
  showAll(): void;
  hideAll(): void;
  getHidden(): number[];
  isolateSelected(): void;
  setColor(target: Target, colour: Colour): void;
  clearColors(): void;
  setBackground(colour: Colour): void;
  toggleXray(): void;
  xrayActive(): boolean;
  getCamera(): Camera;
  /** Any subset; omitted fields keep their value. */
  setCamera(camera: Partial<Camera>): void;
  viewAll(): void;
  frameSelection(): void;
  /** 0 front, 1 back, 2 left, 3 right, 4 top, 5 bottom. */
  setStandardView(id: number): void;
  setNavPreset(preset: "blender" | "rhino" | "revit" | "web"): void;
  stats(): Record<string, any>;
  requestRedraw(): void;
}

type Op = ( ...args: any[] ) => any;

/** Operators by category, each called with the session context. */
interface Ops {
  Viewer: {
    HideSelected( ids?: number[] ): any; ShowSelected( ids?: number[] ): any; ShowAll(): any; HideAll(): any;
    IsolateSelected(): any; Hide( target: Target ): any; Show( target: Target ): any;
    SetVisible( target: Target, visible: boolean ): any;
    SetColor( target: Target, colour: Colour ): any; ClearColors(): any;
    SetSelection( target: Target ): any; AddToSelection( target: Target ): any;
    RemoveFromSelection( target: Target ): any; ClearSelection(): any; SelectGlobalId( guid: string ): any;
    ViewAll(): any; FrameSelection(): any;
    SetStandardView( view: "FRONT" | "BACK" | "LEFT" | "RIGHT" | "TOP" | "BOTTOM" | number ): any;
    SetCamera( camera: Partial<Camera> ): any; SetNavPreset( preset: "blender" | "rhino" | "revit" | "web" ): any;
    ToggleProjection(): any; ToggleFly(): any; ToggleSection(): any; ClearSection(): any;
    ToggleGrid( on?: boolean ): any; ToggleAxes( on?: boolean ): any; ToggleXray(): any;
    ToggleSelectionOutline( on?: boolean ): any;
    Measure( mode: "length" | "area" | "volume" ): any; UndoMeasurePoint(): any; ClearMeasure(): any;
    [ name: string ]: Op;
  };
  Scene: {
    SetBackground( colour: Colour ): any; SetFog( options: Record<string, any> ): any; ToggleFog( on?: boolean ): any;
    SetFederationUnit( unit: { name: string; prefix?: string } ): any;
    SetFalseOrigin( origin: { xyz: [number, number, number]; rzDeg?: number } ): any;
    SetModelTransform( sourceId: number, transform: Record<string, any> ): any; ClearModelTransform( sourceId: number ): any;
    [ name: string ]: Op;
  };
  Project: { Clear(): any; LoadIfc( urlOrFile: string | File, options?: Record<string, any> ): any; ListModels(): any; [ name: string ]: Op };
  [ category: string ]: Record<string, Op> | ( () => string | null );
  /** Undo the last recorded operator; returns its name. */
  undo(): string | null;
  redo(): string | null;
}

type SignalName =
  | "appReady" | "viewerReady" | "modelLoadStarted" | "modelLoaded" | "modelCleared" | "liveIfcReady"
  | "objectPicked" | "selectionChanged" | "selectionCleared"
  | "visibilityChanged" | "viewerToolsChanged" | "measureChanged"
  | "colorsChanged" | "backgroundChanged" | "fogChanged"
  | "cameraChanged" | "standardViewSet" | "navPresetChanged" | "historyChanged";

interface Signal<T = any> {
  add(listener: (payload: T) => void): any;
  addOnce(listener: (payload: T) => void): any;
  remove(listener: (payload: T) => void): void;
  dispatch(payload?: T): void;
}

/** The live Bonsai session. */}
declare module "bonsaiweb" {
  import type { File, EntityInstance } from "ifcopenshell";

  /** The session's data. One entry per model; objects stay in the viewer. */
  export const store: {
    models: Array<{ id: number; name: string; file: File | null }>;
    /** Read-only. Change it with ops.Scene. */
    readonly scene: {
      readonly background: string | null;
      readonly unit: { name: string; prefix: string };
      readonly falseOrigin: { xyz: [number, number, number]; rzDeg: number; explicit: boolean } | null;
      readonly fog: Record<string, any> | null;
      readonly grids: any[];
    };
  };

  /** What is current. Every value is a plain copy or a reference into store. */
  export const context: {
    readonly scene: typeof store.scene;
    readonly camera: Camera | null;
    readonly controls: {
      preset: string; section: boolean; fly: boolean; ortho: boolean; grid: boolean;
      axes: boolean; fog: boolean; xray: boolean; outline: boolean;
    } | null;
    readonly activeObject: ViewerObject | null;
    readonly selectedIds: number[];
    readonly activeModel: { id: number; name: string; file: File | null } | null;
  };

  export const signals: Record<SignalName, Signal>;

  /** bonsai.tool: helpers over the viewer and the open model. */
  export const tool: {
    Viewer: {
      /** The WebGPU viewer. */
      get(): IfcViewer;
    };
    Ifc: {
      /**
       * The live ifcopenshell file. No key: the last file stored.
       * A string is the model name, with or without ".ifc".
       * A number is the viewer model id.
       * The playground opens the model from the top bar before every run.
       */
      get(key?: string | number): File | null;
      /** Live entity for a viewer pick, or context.activeObject. */
      entity(pick?: any): EntityInstance | null;
      globalId(pick?: any): string | null;
    };
    Element: {
      /** [attribute, value] pairs for scalar attributes. */
      getAttributes(entity: EntityInstance): Array<[string, string]>;
      displayName(entity: EntityInstance): string;
    };
    Pset: {
      /** { PsetName: { Prop: value } }, type-inherited values included. */
      getPsets(entity: EntityInstance, options?: { psetsOnly?: boolean; qtosOnly?: boolean; shouldInherit?: boolean }): Record<string, Record<string, string>>;
    };
    Scene: any;
    Loader: any;
    Federation: any;
  };

  /** ops.Category.Name( ...args ), e.g. ops.Viewer.ViewAll(). */
  export const ops: Ops;
}
`;
